import { useMutation } from '@apollo/client';
import {
  LoadingSpinner,
  START_SUPPORT_SESSION,
} from '@employ-engage/common-components';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { doesSessionExist } from 'supertokens-web-js/recipe/session';

/**
 * Where the super admin portal sends an admin once a user approves. Starting
 * the session here, on the main app's own domain, means the new session's
 * cookies land where the app will read them.
 */
export const SupportSessionStartPage = () => {
  const { t } = useTranslation(['common']);
  const { requestId } = useParams();
  const [startSupportSession] = useMutation(START_SUPPORT_SESSION);
  const [error, setError] = useState<string | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!requestId || startedRef.current) return;
    startedRef.current = true;

    (async () => {
      if (!(await doesSessionExist())) {
        setError(t('support-session-sign-in-first'));
        return;
      }
      try {
        await startSupportSession({ variables: { requestId } });
        // A full reload so auth, roles and every cached query load as the user.
        window.location.assign('/');
      } catch (err: any) {
        setError(err?.message || t("Couldn't start the support session."));
      }
    })();
  }, [requestId, startSupportSession, t]);

  if (!error) return <LoadingSpinner spinnerId="support-session-start" />;

  return (
    <div className="mx-auto mt-24 max-w-md rounded-lg bg-white p-6 text-center shadow">
      <h1 className="text-lg font-semibold text-gray-900">
        {t("Couldn't start the support session.")}
      </h1>
      <p className="mt-2 text-sm text-gray-700">{error}</p>
      <Link to="/" className="mt-4 inline-block text-sm text-blue-600 underline">
        {t('Go to Employ Engage')}
      </Link>
    </div>
  );
};

export default SupportSessionStartPage;
